import { serviceSlice } from "../../app/serviceSlice";
import { patientService } from "./nebula.port";
import { IPatient, Tag } from "./patientsSlice";
import { RootState } from "../../app/store";
import { GetPatientsParams, PaginatedPatientsResponse } from "./nebula.service";

const getCachedPatientsArgs = (state: RootState): GetPatientsParams[] => {
    const queries = ((state as any)[serviceSlice.reducerPath]?.queries || {}) as Record<string, any>;

    return Object.values(queries)
        .filter((query) => query?.endpointName === "getPatients" && query?.originalArgs?.trialId)
        .map((query) => query.originalArgs as GetPatientsParams);
};

export const extendedApiSlice = serviceSlice.enhanceEndpoints({ addTagTypes: ["PATIENTS", "TAGS"] }).injectEndpoints({
    endpoints: (builder) => ({
        getPatients: builder.query<PaginatedPatientsResponse, GetPatientsParams>({
            query: (params) => () => patientService.getPatients(params),
            providesTags: ["PATIENTS"],
        }),
        getPatientById: builder.query<IPatient, string>({
            query: (patientId) => () => patientService.getPatientById(patientId),
        }),
        getTags: builder.query<Tag[], string>({
            query: (trialId) => () => patientService.getTags(trialId),
            providesTags: ["TAGS"],
        }),
        addTag: builder.mutation<void, { patientId: string; tag: Tag }>({
            query:
                ({ patientId, tag }) =>
                () =>
                    patientService.addTagToPatient(patientId, tag),
            async onQueryStarted({ patientId, tag }, { dispatch, queryFulfilled, getState }) {
                const state = getState() as RootState;

                const selectedTrial = state.userInfo.selectedTrialId;
                const cachedPatientsArgs = getCachedPatientsArgs(state).filter(
                    (patientsArgs) => !selectedTrial || patientsArgs.trialId === selectedTrial
                );

                const patchResults = cachedPatientsArgs.map((patientsArgs) =>
                    dispatch(
                        extendedApiSlice.util.updateQueryData("getPatients", patientsArgs, (draft) => {
                            draft.patients.forEach((aPatient, i) => {
                                if (aPatient.uuid === patientId) {
                                    draft.patients[i].tags.push(tag);
                                }
                            });
                        })
                    )
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResults.forEach((patchResult) => patchResult.undo());
                }
            },
        }),
        removeTag: builder.mutation<void, { patientId: string; tagId: string }>({
            query:
                ({ patientId, tagId }) =>
                () =>
                    patientService.removeTagFromPatient(patientId, tagId),
            async onQueryStarted({ patientId, tagId }, { dispatch, queryFulfilled, getState }) {
                const state = getState() as RootState;

                const selectedTrial = state.userInfo.selectedTrialId;
                const cachedPatientsArgs = getCachedPatientsArgs(state).filter(
                    (patientsArgs) => !selectedTrial || patientsArgs.trialId === selectedTrial
                );

                const patchResults = cachedPatientsArgs.map((patientsArgs) =>
                    dispatch(
                        extendedApiSlice.util.updateQueryData("getPatients", patientsArgs, (draft) => {
                            draft.patients.forEach((aPatient, i) => {
                                if (aPatient.uuid === patientId) {
                                    draft.patients[i].tags = aPatient.tags.filter((tag) => tag.uuid !== tagId);
                                }
                            });
                        })
                    )
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResults.forEach((patchResult) => patchResult.undo());
                }
            },
        }),
        editPatient: builder.mutation<void, Partial<IPatient>>({
            query: (patient) => () => patientService.editPatient(patient),
            async onQueryStarted(patient, { dispatch, queryFulfilled, getState }) {
                const state = getState() as RootState;

                const selectedTrial = state.userInfo.selectedTrialId;
                const cachedPatientsArgs = getCachedPatientsArgs(state).filter(
                    (patientsArgs) => !selectedTrial || patientsArgs.trialId === selectedTrial
                );

                const patchResults = cachedPatientsArgs.map((patientsArgs) =>
                    dispatch(
                        extendedApiSlice.util.updateQueryData("getPatients", patientsArgs, (draft) => {
                            draft.patients.forEach((aPatient, i) => {
                                if (aPatient.uuid === patient.uuid) {
                                    draft.patients[i] = { ...aPatient, ...patient };
                                }
                            });
                        })
                    )
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResults.forEach((patchResult) => patchResult.undo());
                }
            },
        }),
        removePatient: builder.mutation<void, string>({
            query: (patientId) => () => patientService.removePatient(patientId),
            invalidatesTags: ["PATIENTS"],
        }),
        restorePatient: builder.mutation<void, string>({
            query: (patientId) => () => patientService.restorePatient(patientId),
            invalidatesTags: ["PATIENTS"],
        }),
    }),
});

export const {
    useGetPatientsQuery,
    useGetPatientByIdQuery,
    useEditPatientMutation,
    useGetTagsQuery,
    useAddTagMutation,
    useRemoveTagMutation,
    useRemovePatientMutation,
    useRestorePatientMutation,
} = extendedApiSlice;
